import { useContext, useMemo } from 'react';

import { ImageField } from '@sitecore-jss/sitecore-jss-nextjs';

import { RcnUserContext } from 'src/context/RcnUser/RcnUserContext';

import { ContentAccessLevel } from 'src/types/ContentAccessLevel';
import { ContentDetailAccess } from 'src/types/ContentDetailAccess';

import isAllowedAccessToContent from 'src/utils/isAllowedAccessToContent';

export default function useContentAccessLevel(contentDetailAccess?: ContentDetailAccess): ContentAccessLevel {
	const RcnUser = useContext(RcnUserContext);

	const logoImageField: ImageField | undefined = useMemo(() => {
		if (!contentDetailAccess) {
			return undefined;
		}
		let props: ImageField = {};

		let key: keyof typeof contentDetailAccess;

		for (key in contentDetailAccess) {
			if (contentDetailAccess[key].AllowAccess && contentDetailAccess[key].Logo) {
				props = contentDetailAccess[key].Logo;
				break;
			}
		}

		return props;
	}, [contentDetailAccess]);

	const logoImageStyles =
		logoImageField?.value?.width && logoImageField?.value?.height
			? {
					width: logoImageField.value.width + 'px',
					height: logoImageField.value.height + 'px',
			  }
			: undefined;

	const buttonColour: string | undefined = useMemo(() => {
		if (!contentDetailAccess) {
			return undefined;
		}
		let colour = '';

		let key: keyof typeof contentDetailAccess;

		for (key in contentDetailAccess) {
			if (contentDetailAccess[key].AllowAccess && contentDetailAccess[key].Colour && contentDetailAccess[key].Colour.name) {
				colour = contentDetailAccess[key].Colour.name;
				break;
			}
		}

		return colour;
	}, [contentDetailAccess]);

	const isAllowedAccess = useMemo(() => {
		return isAllowedAccessToContent(contentDetailAccess, RcnUser.authenticatedUserProfile);
	}, [contentDetailAccess, RcnUser, RcnUser.authenticatedUserProfile]);

	const isNonMemberViewingMemberContent = useMemo(() => {
		if (!RcnUser.authenticatedUserProfile || !contentDetailAccess) {
			return false;
		}

		return !RcnUser.authenticatedUserProfile.isActiveMember && contentDetailAccess.RcnMember.AllowAccess;
	}, [RcnUser.authenticatedUserProfile, contentDetailAccess?.RcnMember.AllowAccess]);

	const isNonSubscriberViewingSubscriberContent: boolean = useMemo(() => {
		if (!RcnUser.authenticatedUserProfile || !contentDetailAccess) {
			return false;
		}

		return !RcnUser.authenticatedUserProfile.isActiveRcniSubscriber && contentDetailAccess.RcniPlusSubscriber.AllowAccess;
	}, [RcnUser.authenticatedUserProfile, contentDetailAccess?.RcniPlusSubscriber.AllowAccess]);

	return {
		isAllowedAccessToContent: isAllowedAccess,
		isNonMemberViewingMemberContent: isNonMemberViewingMemberContent,
		isNonSubscriberViewingSubscriberContent: isNonSubscriberViewingSubscriberContent,
		isOpenAccessContent: contentDetailAccess ? contentDetailAccess.OpenAccess.AllowAccess : false,
		isRegisteredUserContent: contentDetailAccess ? contentDetailAccess.RegisteredUser.AllowAccess : false,
		isRcnMemberContent: contentDetailAccess ? contentDetailAccess.RcnMember.AllowAccess : false,
		isRcniPlusSubscriberContent: contentDetailAccess ? contentDetailAccess?.RcniPlusSubscriber.AllowAccess : false,
		buttonColour,
		logoImageField,
		logoImageStyles,
	};
}
